import {
  getTeam,
  getTeamPermissions,
} from "@/gen_src/proto/chalk/server/v1/team-TeamService_connectquery";
import { ONBOARDING_REQUIREMENT_QUERY_META } from "@/components/onboarding/onboarding-query";
import {
  GetTeamPermissionsResponse,
  GetTeamResponse,
  Team,
} from "@/gen_src/proto/chalk/server/v1/team_pb";
import { TeamService, TeamServiceImpl, TeamServiceNullImpl } from "@/utils/teamService";
import { ConnectError } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import React, { PropsWithChildren, createContext, useContext, useMemo } from "react";

export interface TeamContextValue {
  team?: Team;
  teamPermissions?: GetTeamPermissionsResponse;
  teamError?: ConnectError;
  teamPermissionsError?: ConnectError;
  teamLoading: boolean;
  teamPermissionsLoading: boolean;
  teamService: TeamService;
  refetchTeamQuery: (
    options?: RefetchOptions | undefined,
  ) => Promise<QueryObserverResult<GetTeamResponse, ConnectError>>;
  refetchTeamPermissionsQuery: (
    options?: RefetchOptions | undefined,
  ) => Promise<QueryObserverResult<GetTeamPermissionsResponse, ConnectError>>;
}

export const TeamContext = createContext<TeamContextValue | undefined>(undefined);

export const TeamProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const teamQuery = useQuery(getTeam, {}, { meta: ONBOARDING_REQUIREMENT_QUERY_META });
  const teamPermissionsQuery = useQuery(getTeamPermissions);

  const team = teamQuery.data?.team;
  const teamPermissions = teamPermissionsQuery.data;

  const teamService = useMemo(
    () =>
      team != null && teamPermissions != null
        ? new TeamServiceImpl(team, teamPermissions)
        : TeamServiceNullImpl,
    [team, teamPermissions],
  );

  const contextValue = useMemo(
    () => ({
      team,
      teamPermissions,
      teamError: teamQuery.error ?? undefined,
      teamPermissionsError: teamPermissionsQuery.error ?? undefined,
      // isPending (not isLoading): these queries are always enabled, and consumers use
      // these flags as initial-content gates. isLoading is false on the first frame
      // before fetching starts, which would flash unloaded content.
      teamLoading: teamQuery.isPending,
      teamPermissionsLoading: teamPermissionsQuery.isPending,
      teamService,
      refetchTeamQuery: teamQuery.refetch,
      refetchTeamPermissionsQuery: teamPermissionsQuery.refetch,
    }),
    [
      team,
      teamPermissions,
      teamQuery.error,
      teamQuery.isPending,
      teamQuery.refetch,
      teamPermissionsQuery.error,
      teamPermissionsQuery.isPending,
      teamPermissionsQuery.refetch,
      teamService,
    ],
  );

  return <TeamContext.Provider value={contextValue}>{children}</TeamContext.Provider>;
};

const emptyTeamQueryResult = async <TData,>(): Promise<QueryObserverResult<TData, ConnectError>> =>
  ({ data: undefined, error: undefined }) as unknown as QueryObserverResult<TData, ConnectError>;

/**
 * Only intended for use in /test-components
 */
export const MockTeamProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const mockTeamData: TeamContextValue = useMemo((): TeamContextValue => {
    return {
      team: undefined,
      teamPermissions: undefined,
      teamError: undefined,
      teamPermissionsError: undefined,
      teamLoading: false,
      teamPermissionsLoading: false,
      teamService: TeamServiceNullImpl,
      refetchTeamQuery: () => emptyTeamQueryResult<GetTeamResponse>(),
      refetchTeamPermissionsQuery: () => emptyTeamQueryResult<GetTeamPermissionsResponse>(),
    };
  }, []);
  return <TeamContext.Provider value={mockTeamData}>{children}</TeamContext.Provider>;
};

export const useTeamContext = (): TeamContextValue => {
  const contextValue = useContext(TeamContext);
  if (contextValue == null) {
    throw new Error("useTeamContext must be used within a TeamProvider");
  }
  return contextValue;
};
